import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, tap } from 'rxjs/operators';

interface Config {
  throttleTimeMs: number
}

export class VirtualScrollContextService {
  private scrollingSubject = new Subject();
  private subscriptions: Subscription[] = [];
  private displayFakeSubject$ = new BehaviorSubject<boolean>(false);
  private isScrollingCache$: Observable<boolean>;
  private config: Config;
  private readonly sizesData = {
    sizesMap: new Map(),
    firstSize: null
  };

  init(config: Config): void {
    this.config = config;

    if (this.isEnabled()) {
      this.subscriptions.push(
        this.scrollingSubject
          .pipe(
            tap(() => this.displayFakeSubject$.next(true)),
            debounceTime(config.throttleTimeMs)
          )
          .subscribe(() => {
            this.displayFakeSubject$.next(false);
          })
      );
    }
  }

  onScroll(): void {
    this.scrollingSubject.next(undefined);
  }

  isEnabled(): boolean {
    return this.config?.throttleTimeMs > 0;
  }

  isScrolling(): Observable<boolean> {
    if (!this.isScrollingCache$) {
      this.isScrollingCache$ = this.displayFakeSubject$.pipe(distinctUntilChanged(), delay(0));
    }

    return this.isScrollingCache$;
  }

  isScrollingSync(): boolean {
    return this.displayFakeSubject$.value;
  }

  setSize(params: { itemId: string, height: number, width: number }): void {
    const size = { height: params.height, width: params.width };

    if (!this.sizesData.firstSize) {
      this.sizesData.firstSize = size;
    }

    this.sizesData.sizesMap.set(params.itemId, size);
  }

  getSize(itemId: string): { height: number, width: number } {
    return this.sizesData.sizesMap.get(itemId) || this.sizesData.firstSize || { height: 0, width: 0 };
  }

  dispose(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
